import { IBounds } from "./Bounds.interface";
import type { IAreaAspectRatio } from "./Areas.interface";

interface IStudioOverlayBase {
  id: string;
  label: string;
  active: boolean;
}

export type IOverlayAspectRatio = IAreaAspectRatio | "free";

export enum EStudioOverlayType {
  CUSTOM = "custom",
}

export type IOverlayElementAnimationKind =
  | "fade-in"
  | "slide-in-top"
  | "slide-in-right"
  | "slide-in-bottom"
  | "slide-in-left"
  | "scale-in"
  | "pulse";

/** Animación de catálogo (entrada + pulse), editable desde el OverlayBuilder. */
export interface IOverlayElementAnimationPreset {
  kind: IOverlayElementAnimationKind;
  durationMs: number;
  delayMs?: number;
  easing?: "linear" | "ease" | "ease-in" | "ease-out" | "ease-in-out";
  /** For "pulse": loop forever. For others: play once on mount. */
  loop?: boolean;
}

/** Stop de un track de keyframes: `at` es la fase 0..1 del ciclo. */
export interface IOverlayKeyframeStop {
  at: number;
  value: number;
}

/**
 * Animación descriptiva por keyframes (la emite la tool ai-overlays al partir
 * el chrome de una escena en capas: cada capa animada trae sus tracks). Es
 * infinita y la reproducen igual el DOM (@keyframes CSS) y Pixi (updater).
 * Semántica: antes de `delayMs` el elemento queda en los valores de `at=0`;
 * cada track se interpola linealmente entre sus stops con `easing` POR
 * SEGMENTO (como animation-timing-function); track ausente = identidad.
 * Los tracks que comparten propiedad CSS (x/y → `translate`; scale·scaleX·
 * scaleY → `scale`) se fusionan sobre la unión de sus `at`, como los ve el
 * @keyframes; con varios stops en el mismo `at` gana el último.
 * Orden de transformación: translate → rotate → scale alrededor de `origin`.
 */
export interface IOverlayElementKeyframes {
  kind: "keyframes";
  durationMs: number;
  delayMs?: number;
  easing?: "linear" | "ease" | "ease-in" | "ease-out" | "ease-in-out";
  loop: true;
  /** animation-direction: alternate (ida y vuelta, período 2×duration). */
  alternate?: boolean;
  /** Origen de rotación/escala en px de diseño RELATIVO a bounds.left/top del elemento. Default: centro del elemento. */
  origin?: { x: number; y: number };
  tracks: {
    opacity?: IOverlayKeyframeStop[];
    /** translate en px de diseño (mismas unidades que bounds). */
    x?: IOverlayKeyframeStop[];
    y?: IOverlayKeyframeStop[];
    /** grados */
    rotate?: IOverlayKeyframeStop[];
    /** ambos ejes; se multiplica con scaleX/scaleY si vienen */
    scale?: IOverlayKeyframeStop[];
    scaleX?: IOverlayKeyframeStop[];
    scaleY?: IOverlayKeyframeStop[];
  };
}

export type IOverlayElementAnimation =
  | IOverlayElementAnimationPreset
  | IOverlayElementKeyframes;

/** Stops for a gradient fill. position is 0-100. */
export interface IOverlayGradientStop {
  position: number;
  color: string;
}

export type IOverlayFill =
  | { kind: "solid"; color: string }
  | { kind: "linear"; angle: number; stops: IOverlayGradientStop[] }
  | { kind: "radial"; stops: IOverlayGradientStop[] };

export interface IOverlayGlow {
  color: string;
  /** Blur radius in design px */
  blur: number;
  /** Optional spread (additional drop-shadow before the main one for stronger neon). */
  spread?: number;
}

export type IOverlayStrokeStyle = "solid" | "dashed" | "dotted" | "double";

/**
 * Per-element timing override for alerts. When set, the element animates
 * INSIDE the parent alert's enter→hold→exit lifecycle:
 *  - `offsetMs` delays the element's appearance from the alert's enter start
 *  - `durationMs` cuts the element off early (defaults to "until alert exits")
 *
 * Has no effect outside the alert runtime — overlays render statically.
 */
export interface IOverlayElementTiming {
  /** ms delay from the alert's enter stage start before this element shows. */
  offsetMs?: number;
  /** ms the element stays visible. Default: parent alert duration. */
  durationMs?: number;
}

/**
 * Stylized shader-like preset that wraps any element. Implementations vary
 * per renderer — DOM uses CSS filter chains + masks, the Pixi pipeline can
 * swap in a real shader for the same preset id.
 *
 * Adding a new preset is intentionally a closed enum: presets ship as a
 * curated catalog, not free-form, so we can guarantee both renderers cover
 * them and so the picker UI stays meaningful.
 */
export type IOverlayShaderPresetKind =
  | "neon"
  | "holographic"
  | "pixelate"
  | "scanlines"
  | "glitch"
  | "ascii";

export interface IOverlayShaderPreset {
  kind: IOverlayShaderPresetKind;
  /** Generic intensity 0–100. Each preset interprets it (blur radius, glitch jitter, scanline density). */
  intensity?: number;
}

interface IOverlayElementBase {
  id: string;
  bounds: IBounds;
  /** Nombre editable de la capa (lo muestra el panel de capas). */
  name?: string;
  /** Capa padre (grupo) en el panel de capas. Sólo organización del editor. */
  group?: string;
  rotation?: number;
  hidden?: boolean;
  locked?: boolean;
  animation?: IOverlayElementAnimation;
  glow?: IOverlayGlow;
  /**
   * Conditional visibility. A variable token (e.g. `last.donation.message`).
   * If the resolved value is empty/falsy at render time, the element is
   * skipped. Useful for donation messages that may or may not be present.
   */
  showIf?: string;
  /** Per-element timing override (alert runtime only). */
  timing?: IOverlayElementTiming;
  /**
   * Stylized "filter" preset applied on top of the element's own rendering.
   * Examples: neon glow, holographic gradient, pixelate, scanlines, glitch
   * displacement. Stacks on top of the element's own options (color, font,
   * etc).
   */
  shaderPreset?: IOverlayShaderPreset;
}

export interface IOverlayTextStroke {
  color: string;
  /** Stroke width in design px. */
  width: number;
}

export interface IOverlayTextTypewriter {
  /** Characters per second. Default 20. Only applied during the alert enter stage. */
  charsPerSecond?: number;
}

/**
 * SVG-style cubic Bezier path the text follows. The path coordinates are in
 * the element's local space (0..bounds.width / 0..bounds.height). Renderers
 * that support text-on-path (DOM via `<svg><textPath>`) render glyphs along
 * this curve; renderers that don't fall back to flat text.
 */
export interface IOverlayTextPath {
  /** SVG path `d` attribute string, e.g. "M 0 50 Q 100 0 200 50". */
  d: string;
  /** Starting offset along the path (0–100 percent). Default 0. */
  startOffset?: number;
}

export interface IOverlayElementText extends IOverlayElementBase {
  kind: "text";
  options: {
    text: string;
    fontFamily?: string;
    fontSize?: string;
    fontColor?: string;
    /**
     * Optional gradient fill for the text. When set, takes precedence over
     * `fontColor` in renderers that support it (Pixi + DOM via
     * background-clip: text). Solid kind is treated as `fontColor` fallback.
     */
    fontGradient?: IOverlayFill;
    backgroundColor?: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    textAlign?: "left" | "center" | "right";
    /**
     * CSS letter-spacing as a `<length>` (`"2px"`, `"0.1em"`) or `"normal"`.
     * Renderers that support it apply the gap between glyphs; the Pixi path
     * additionally compensates the trailing-glyph advance (see
     * `parseLetterSpacingPx` / `applyPixiLetterSpacingCompensation`).
     */
    letterSpacing?: string;
    /** Outline / stroke around the text glyphs. */
    stroke?: IOverlayTextStroke;
    /** Type-on animation; renderers that support it reveal characters over time. */
    typewriter?: IOverlayTextTypewriter;
    /** Render text along an SVG cubic Bezier path. */
    textPath?: IOverlayTextPath;
    /**
     * Decorative chrome text — renders inside the overlay but is NOT promoted
     * to a first-class `IStudioElement` by `buildStudioFromSceneTemplate` on
     * apply. Used for atmospheric labels (corner badges, repeating borders,
     * status chrome) that the design author wants visible but doesn't want
     * surfacing to the streamer's Elements panel as editable copy. The
     * streamer can still edit them by drilling into the OverlayBuilder.
     *
     * Without this flag, alternative would be baking the text into a bitmap
     * bg — which loses font rendering (browsers don't process @font-face
     * for SVGs loaded via `<img src="data:...">`).
     */
    readOnly?: boolean;
    /**
     * Per-language translations of `text` (the source copy is Spanish). Seeded
     * scene templates ship this so the picker preview and the applied scene can
     * render in the streamer's UI language. Resolved by `localizeOverlayText` /
     * `buildStudioFromSceneTemplate` — `text` (es) is the fallback when a
     * language is missing (e.g. symbols/numbers/already-English chrome carry no
     * `i18n` entry and render identically in every language).
     */
    i18n?: { en?: string; pt?: string };
    /**
     * Force the text onto a single line that may overflow the bounds
     * horizontally. Used by border-style chrome (`INTERMISSION ·
     * INTERMISSION ·` repeats, `·N·N·N·` punctuation borders) where the SVG
     * intent is "display until you run out of space"; without this the
     * default `word-break: break-word` wraps the text into a multi-line
     * block and loses the visual character. Renderer applies
     * `white-space: nowrap; overflow: hidden`.
     */
    noWrap?: boolean;
  };
}

export interface IOverlayElementImage extends IOverlayElementBase {
  kind: "image";
  options: {
    src: string;
    fit: "contain" | "cover" | "fill" | "9-slice";
    sliceRect?: Omit<IBounds, "zIndex">;
  };
}

/**
 * Video element: un `<video>` texturizado en un rect, con `fit` igual que la
 * imagen. Reusa el manejo de textura de video de Pixi (VideoSource auto-
 * actualizada, como la cámara/mask). Pensado para marcos/chrome animados.
 * Sólo por URL (no se inlinea como data-URL — los videos son pesados).
 */
export interface IOverlayElementVideo extends IOverlayElementBase {
  kind: "video";
  options: {
    src: string;
    fit: "contain" | "cover" | "fill";
    /** Loop continuo. Default true. */
    loop?: boolean;
    /** Mutear el audio. Default true (los overlays son visuales). */
    muted?: boolean;
  };
}

export interface IOverlayElementShape extends IOverlayElementBase {
  kind: "shape";
  options: {
    shape:
      | "rect"
      | "rounded-rect"
      | "circle"
      | "hexagon"
      | "parallelogram"
      | "chevron";
    fill?: string | IOverlayFill;
    stroke?: string;
    strokeWidth?: number;
    strokeStyle?: IOverlayStrokeStyle;
    borderRadius?: number;
  };
}

/**
 * GPU-style particle systems. Tuned for alert celebrations: confetti rain
 * for cheers, fireworks for raids, hearts for follows, coins for donations.
 *
 * `preset` selects the look + physics; `intensity` is a 0–100 dial that
 * scales emission rate, particle count cap and gravity.
 */
export type IOverlayParticlePreset =
  | "confetti"
  | "fireworks"
  | "hearts"
  | "coins"
  | "sparkles"
  // Ambientales para fondos de escena (llovizna, nieve, polvo, cielo, agua, niebla).
  | "rain"
  | "snow"
  | "dust"
  | "stars"
  | "bubbles"
  | "clouds";

export interface IOverlayElementParticles extends IOverlayElementBase {
  kind: "particles";
  options: {
    preset: IOverlayParticlePreset;
    /** 0–100. Drives spawn rate + max concurrent particles. Default 60. */
    intensity?: number;
    /** Tint color override; falls back to preset defaults when omitted. */
    color?: string;
    /** Whether the system loops continuously. Default true; alerts can override per-element timing. */
    loop?: boolean;
  };
}

/**
 * Procedural "infinite background" patterns rendered via animated frag-shader-
 * equivalent JS in the DOM, or a real fragment shader in Pixi. Solid colors
 * stay simpler with shape elements; this is for animated atmosphere.
 */
export type IOverlayProceduralPattern =
  | "noise"
  | "plasma"
  | "gradient-flow"
  | "voronoi"
  /** Banda de luz que barre verticalmente; transparente fuera de la banda. */
  | "scanline";

export interface IOverlayElementProcedural extends IOverlayElementBase {
  kind: "procedural";
  options: {
    pattern: IOverlayProceduralPattern;
    /** Two-color palette for patterns that mix between extremes. */
    colorA?: string;
    colorB?: string;
    /** Animation speed 0–100. Default 50. */
    speed?: number;
    /** Pattern scale 0–100 (interpreted per-pattern, e.g. noise frequency). Default 50. */
    scale?: number;
    /**
     * Opacidad 0–1 del patrón (default 1). Permite usarlo como capa ambiental
     * encima de un fondo (p. ej. gradient-flow al 0.15 = "gradiente que respira").
     */
    opacity?: number;
  };
}

export type IOverlayElement =
  | IOverlayElementText
  | IOverlayElementImage
  | IOverlayElementVideo
  | IOverlayElementShape
  | IOverlayElementParticles
  | IOverlayElementProcedural;

export interface OverlayPollingConfig {
  /** ms entre polls de getViewers — default 30000 */
  viewers?: number;
  /** ms del tick global (afecta `random`, `tick`) — default 1000 */
  tick?: number;
  /** ms entre polls de YouTube live chat — default 3000 */
  youtubeChat?: number;
  /** ms entre polls de YouTube subs/members — default 60000 */
  youtubeSubs?: number;
  /** ms para actualización de now/today — default 1000 */
  clock?: number;
  /** ms entre polls de events.* desde Dexie — default 1000 */
  eventsRefresh?: number;
}

/**
 * Bounds of the area's media (webcam / image / video) the overlay was
 * authored to wrap. Expressed as percentages of the area (0..100) to
 * match `IStudioArea.media.bounds`. When present, applying the overlay
 * snaps the area's media to these bounds automatically — used by webcam-
 * frame overlays where the camera should slot into a transparent cutout
 * in the frame chrome. Omit for overlays that don't wrap a source (HUDs,
 * lower-thirds, alerts).
 */
export interface IOverlaySourceSlot {
  left: number;
  top: number;
  width: number;
  height: number;
  /**
   * CSS `clip-path` value applied to the area's media after snap, so the
   * camera/image gets clipped to the overlay's actual cutout SHAPE — not
   * just a rectangle. Required for designs with non-rectangular cutouts
   * (octagonal hex frames, blob shapes, circles). Standard CSS syntax:
   * `polygon(0% 0%, 100% 0%, ...)`, `circle(50%)`, `path('M ...')`. The
   * percentages are relative to the snapped media's bounding box (= the
   * sourceSlot rectangle), NOT the area or design canvas.
   */
  clipPath?: string;
}

export interface IStudioOverlayCustom extends IStudioOverlayBase {
  type: EStudioOverlayType.CUSTOM;
  options: {
    elements: IOverlayElement[];
    designAspectRatio?: IOverlayAspectRatio;
    /**
     * Px del design-space autorado; si falta, el canvas se deriva de
     * designAspectRatio. Hoy sólo lo setean composiciones de alert
     * re-autoradas a un espacio local (catálogo del Store).
     */
    designSize?: { width: number; height: number };
    variables?: Record<string, unknown>;
    pollingConfig?: OverlayPollingConfig;
    source?: { storeId: string; slug: string; version: string };
    sourceSlot?: IOverlaySourceSlot;
    /**
     * Registro de capas (grupos) creadas por el usuario, incluidas las vacías.
     * El panel de capas agrupa por `element.group`; este registro mantiene las
     * capas sin elementos visibles + su orden, y alimenta el selector "Capa".
     */
    groups?: string[];
  };
}

export type IStudioOverlay = IStudioOverlayCustom;
